import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  setDoc,
} from 'firebase/firestore';
import { InvoiceDraft } from '../../types';
import { getFirebaseFirestore } from '../firebase';
import { hydrateInvoice } from '../invoice';
import { upsertCustomerFromInvoice, findCustomerIdForInvoice, invoiceCustomerId } from './customers';
import { getDB, resolveUid, toFirestoreData } from './idb';
import { sortByCreatedAsc } from '../sort';

function invoicesCol(uid: string) {
  const fs = getFirebaseFirestore();
  if (!fs) throw new Error('Firestore is not configured');
  return collection(fs, 'users', uid, 'invoices');
}

function sortInvoices(invoices: InvoiceDraft[]): InvoiceDraft[] {
  return sortByCreatedAsc(invoices);
}

export async function saveInvoice(
  invoice: InvoiceDraft,
  uid?: string | null,
  options?: { upsertCustomer?: boolean },
): Promise<InvoiceDraft> {
  const owner = resolveUid(uid);
  let toSave = invoice;

  if (owner) {
    if (!invoiceCustomerId(toSave)) {
      const linkedId = await findCustomerIdForInvoice(toSave, owner);
      if (linkedId) {
        toSave = { ...toSave, customerId: linkedId };
      }
    }

    await setDoc(doc(invoicesCol(owner), toSave.id), toFirestoreData(toSave));

    if (options?.upsertCustomer) {
      const customer = await upsertCustomerFromInvoice(toSave, owner);
      if (customer?.id && toSave.customerId !== customer.id) {
        toSave = { ...toSave, customerId: customer.id };
        await setDoc(
          doc(invoicesCol(owner), toSave.id),
          toFirestoreData(toSave),
        );
      }
    }

    return toSave;
  }

  const db = await getDB();
  await db.put('invoices', invoice);
  return invoice;
}

export async function getInvoice(
  id: string,
  uid?: string | null,
): Promise<InvoiceDraft | undefined> {
  const owner = resolveUid(uid);
  if (owner) {
    const snap = await getDoc(doc(invoicesCol(owner), id));
    return snap.exists() ? hydrateInvoice(snap.data() as InvoiceDraft) : undefined;
  }
  const db = await getDB();
  const local = await db.get('invoices', id);
  return local ? hydrateInvoice(local) : undefined;
}

export async function getAllInvoices(uid?: string | null): Promise<InvoiceDraft[]> {
  const owner = resolveUid(uid);
  if (owner) {
    const snap = await getDocs(invoicesCol(owner));
    return sortInvoices(snap.docs.map((d) => hydrateInvoice(d.data() as InvoiceDraft)));
  }
  const db = await getDB();
  const local = await db.getAll('invoices');
  return sortInvoices((local ?? []).map(hydrateInvoice));
}

export async function deleteInvoice(
  id: string,
  uid?: string | null,
): Promise<void> {
  const owner = resolveUid(uid);
  if (owner) {
    await deleteDoc(doc(invoicesCol(owner), id));
    return;
  }
  const db = await getDB();
  await db.delete('invoices', id);
}

export async function deleteAllInvoices(uid?: string | null): Promise<void> {
  const owner = resolveUid(uid);
  if (owner) {
    const snap = await getDocs(invoicesCol(owner));
    await Promise.all(snap.docs.map((d) => deleteDoc(d.ref)));
    return;
  }
  const db = await getDB();
  const tx = db.transaction('invoices', 'readwrite');
  await tx.objectStore('invoices').clear();
  await tx.done;
}
